import { useEffect, useState } from "react";
import type { JSX } from "react";
import DataTable, { DATA_TABLE_CLASSES } from "@/components/common/DataTable";
import { formatDate, formatNumber } from "../profile/format";
import { labelForProperty } from "../profile/labels";
import type { DeviceAggregateRow, DeviceListRow } from "../types";
import { resolvePlatformName } from "../utils";

type DeviceTableProps = {
  rows: DeviceListRow[];
  onOpenDevice: (deviceId: string) => void;
  isLoading?: boolean;
  aggregates: Record<string, DeviceAggregateRow>;
  aggregatesLoading: boolean;
};

const truncate = (value: string | null | undefined, max: number): string => {
  if (!value) return "—";
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
};

const LoadingDots = (): JSX.Element => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setPhase((p) => (p + 1) % 3), 350);
    return () => window.clearInterval(id);
  }, []);
  return (
    <span
      aria-label="Loading"
      className="font-mono text-(--text-muted) tracking-widest"
    >
      {".".repeat(phase + 1).padEnd(3, " ")}
    </span>
  );
};

const Cell = ({
  agg,
  loading,
  render,
}: {
  agg: DeviceAggregateRow | undefined;
  loading: boolean;
  render: (row: DeviceAggregateRow) => JSX.Element | string;
}): JSX.Element => {
  if (agg) return <>{render(agg)}</>;
  if (loading) return <LoadingDots />;
  return <>—</>;
};

export const DeviceTable = ({
  rows,
  onOpenDevice,
  isLoading = false,
  aggregates,
  aggregatesLoading,
}: DeviceTableProps): JSX.Element => {
  return (
    <DataTable>
      <thead>
        <tr className={DATA_TABLE_CLASSES.headRow}>
          <th className={DATA_TABLE_CLASSES.th}>Device ID</th>
          <th className={DATA_TABLE_CLASSES.th}>Last seen</th>
          <th className={DATA_TABLE_CLASSES.th}>First seen</th>
          <th className={DATA_TABLE_CLASSES.th}>Ads</th>
          <th className={DATA_TABLE_CLASSES.th}>Last IP</th>
          <th className={DATA_TABLE_CLASSES.th}>Country</th>
          <th className={DATA_TABLE_CLASSES.th}>Platform</th>
          <th className={DATA_TABLE_CLASSES.th}>Interests</th>
          <th className={DATA_TABLE_CLASSES.thRight}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={9} className={DATA_TABLE_CLASSES.emptyCell}>
              {isLoading ? "Loading devices…" : "No devices found."}
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            const agg = aggregates[row.deviceId];
            return (
              <tr
                key={row.deviceId}
                onDoubleClick={() => onOpenDevice(row.deviceId)}
                className={`${DATA_TABLE_CLASSES.bodyRow} cursor-pointer`}
              >
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13.5px] font-bold text-(--text-primary)`}
                >
                  <span title={row.deviceId} className="font-mono">
                    {truncate(row.deviceId, 15)}
                  </span>
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13px] text-(--text-secondary)`}
                >
                  <Cell
                    agg={agg}
                    loading={aggregatesLoading}
                    render={(r) => (r.lastSeen ? formatDate(r.lastSeen) : "—")}
                  />
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13px] text-(--text-secondary)`}
                >
                  <Cell
                    agg={agg}
                    loading={aggregatesLoading}
                    render={(r) =>
                      r.firstSeen ? formatDate(r.firstSeen) : "—"
                    }
                  />
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13.5px] font-bold text-(--text-primary) tabular-nums`}
                >
                  <Cell
                    agg={agg}
                    loading={aggregatesLoading}
                    render={(r) => formatNumber(r.vastCount)}
                  />
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13px] text-(--text-secondary)`}
                >
                  <Cell
                    agg={agg}
                    loading={aggregatesLoading}
                    render={(r) => truncate(r.lastIp, 24)}
                  />
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13px] text-(--text-secondary)`}
                >
                  <Cell
                    agg={agg}
                    loading={aggregatesLoading}
                    render={(r) => r.lastCountry ?? "—"}
                  />
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13px] font-semibold text-(--text-secondary)`}
                >
                  <Cell
                    agg={agg}
                    loading={aggregatesLoading}
                    render={(r) => resolvePlatformName(r.lastPlatform)}
                  />
                </td>
                <td
                  className={`${DATA_TABLE_CLASSES.td} text-[13px] text-(--text-secondary)`}
                >
                  <Cell
                    agg={agg}
                    loading={aggregatesLoading}
                    render={(r) =>
                      r.interests.length > 0
                        ? r.interests
                            .map((value) => labelForProperty("interest", value))
                            .join(", ")
                        : "—"
                    }
                  />
                </td>
                <td className={DATA_TABLE_CLASSES.tdRight}>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenDevice(row.deviceId);
                    }}
                    className="inline-flex items-center gap-2 rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-3 py-1.5 text-[12px] font-semibold text-(--text-primary) outline-none transition-colors hover:border-(--brand-primary) hover:text-(--brand-primary) focus:outline-none"
                  >
                    View
                  </button>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </DataTable>
  );
};
